import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { OrganizerItem, CreateOrganizerRequest } from '../../types';
import { retreatService } from '../../services/retreatService';

interface OrganizerState {
  organizers: OrganizerItem[];
  loading: boolean;
  error: string | null;
}

const initialState: OrganizerState = {
  organizers: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchOrganizers = createAsyncThunk(
  'organizer/fetchOrganizers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await retreatService.getOrganizers();
      return response.data.data; // Extract the actual data array from the paginated response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch organizers');
    }
  }
);

export const createOrganizer = createAsyncThunk(
  'organizer/createOrganizer',
  async (organizerData: CreateOrganizerRequest, { rejectWithValue }) => {
    try {
      const response = await retreatService.createOrganizer(organizerData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create organizer');
    }
  }
);

const organizerSlice = createSlice({
  name: 'organizer',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetOrganizers: (state) => {
      state.organizers = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch organizers
      .addCase(fetchOrganizers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizers.fulfilled, (state, action) => {
        state.loading = false;
        state.organizers = action.payload;
      })
      .addCase(fetchOrganizers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create organizer
      .addCase(createOrganizer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrganizer.fulfilled, (state, action) => {
        state.loading = false;
        state.organizers.push(action.payload);
      })
      .addCase(createOrganizer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetOrganizers } = organizerSlice.actions;
export default organizerSlice.reducer;
